<template>
  <div class="handover-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('handover.page.title')" :subtitle="$t('handover.page.subtitle')">
      <template #extra>
        <el-button type="primary" size="mini" icon="el-icon-search" @click="selectOwner">
          {{ $t('handover.select') }}
        </el-button>
        <el-button size="mini" icon="el-icon-back" @click="goBack">
          {{ $t('handover.back') }}
        </el-button>
      </template>
    </ds-page-header>

    <!-- 业主信息 -->
    <div class="ds-panel">
      <ds-toolbar :total="null" :show-column-setting="false" :show-density="false" :show-refresh="false">
        <template #left>
          <span class="ds-panel__title">{{ $t('handover.ownerInfo') }}</span>
        </template>
      </ds-toolbar>

      <div class="ds-panel__body">
        <el-form>
          <el-row :gutter="16">
            <el-col :xs="24" :sm="12" :md="6">
              <el-form-item :label="$t('handover.name')" label-position="top">
                <el-input v-model="handoverInfo.name" :placeholder="$t('handover.namePlaceholder')" clearable />
              </el-form-item>
            </el-col>
            <el-col :xs="24" :sm="12" :md="6">
              <el-form-item :label="$t('handover.gender')" label-position="top">
                <el-select v-model="handoverInfo.sex" :placeholder="$t('handover.genderPlaceholder')" class="w-full">
                  <el-option :label="$t('handover.male')" value="0" />
                  <el-option :label="$t('handover.female')" value="1" />
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :xs="24" :sm="12" :md="6">
              <el-form-item :label="$t('handover.age')" label-position="top">
                <el-input v-model="handoverInfo.age" type="number" :placeholder="$t('handover.agePlaceholder')"
                  clearable />
              </el-form-item>
            </el-col>
          </el-row>
          <el-row :gutter="16">
            <el-col :xs="24" :sm="12" :md="6">
              <el-form-item :label="$t('handover.idCard')" label-position="top">
                <el-input v-model="handoverInfo.idCard" :placeholder="$t('handover.idCardPlaceholder')" clearable />
              </el-form-item>
            </el-col>
            <el-col :xs="24" :sm="12" :md="6">
              <el-form-item :label="$t('handover.phone')" label-position="top">
                <el-input v-model="handoverInfo.link" type="number" :placeholder="$t('handover.phonePlaceholder')"
                  clearable />
              </el-form-item>
            </el-col>
            <el-col :xs="24" :sm="12" :md="12">
              <el-form-item :label="$t('handover.remark')" label-position="top">
                <el-input v-model="handoverInfo.remark" :placeholder="$t('handover.remarkPlaceholder')" clearable />
              </el-form-item>
            </el-col>
          </el-row>
        </el-form>
      </div>
    </div>

    <!-- 房屋信息 -->
    <div class="ds-panel">
      <ds-toolbar :total="handoverInfo.rooms.length" :show-column-setting="false" :show-density="false"
        :show-refresh="false">
        <template #left>
          <span class="ds-panel__title">{{ $t('handover.houseInfo') }}</span>
        </template>
        <template #right>
          <el-button type="primary" size="mini" icon="el-icon-plus" @click="selectRoom">
            {{ $t('handover.selectHouse') }}
          </el-button>
          <el-button size="mini" icon="el-icon-money" @click="createFeeOrder">
            {{ $t('handover.createFee') }}
          </el-button>
        </template>
      </ds-toolbar>

      <ds-data-table :data="handoverInfo.rooms" border :empty-description="$t('handover.page.emptyRoom')">
        <el-table-column prop="roomInfo" :label="$t('handover.house')" align="center" width="180">
          <template slot-scope="scope">
            <span class="ds-num">
              {{ scope.row.floorNum }}-{{ scope.row.unitNum }}-{{ scope.row.roomNum }}
            </span>
          </template>
        </el-table-column>
        <el-table-column prop="builtUpArea" :label="$t('handover.area')" align="right" width="150">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.builtUpArea }} {{ $t('handover.squareMeter') }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="stateName" :label="$t('handover.houseStatus')" align="center" width="120">
          <template slot-scope="scope">
            <ds-status-tag :type="resolveRoomState(scope.row)" :label="scope.row.stateName || '-'" />
          </template>
        </el-table-column>
        <el-table-column :label="$t('handover.operation')" align="center" width="100">
          <template slot-scope="scope">
            <el-button size="mini" type="text" class="ds-btn-danger" @click="openDelRoomModel(scope.row)">
              {{ $t('handover.delete') }}
            </el-button>
          </template>
        </el-table-column>
      </ds-data-table>
    </div>

    <!-- 费用信息 -->
    <div v-if="handoverInfo.fees && handoverInfo.fees.length > 0" class="ds-panel">
      <ds-toolbar :total="handoverInfo.fees.length" :show-column-setting="false" :show-density="false"
        :show-refresh="false">
        <template #left>
          <span class="ds-panel__title">{{ $t('handover.feeInfo') }}</span>
        </template>
      </ds-toolbar>

      <ds-data-table :data="handoverInfo.fees" border>
        <el-table-column prop="feeName" :label="$t('handover.feeItem')" align="left" min-width="160"
          show-overflow-tooltip />
        <el-table-column prop="startTime" :label="$t('handover.startTime')" align="center" width="170" />
        <el-table-column prop="endTime" :label="$t('handover.endTime')" align="center" width="170">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.endTime || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('handover.operation')" align="center" width="100">
          <template slot-scope="scope">
            <el-button size="mini" type="text" class="ds-btn-danger" @click="openDelFeeModel(scope.row)">
              {{ $t('handover.delete') }}
            </el-button>
          </template>
        </el-table-column>
      </ds-data-table>
    </div>

    <div class="submit-btn">
      <el-button type="primary" @click="saveHandoverInfo">
        <i class="el-icon-check"></i>&nbsp;{{ $t('handover.submit') }}
      </el-button>
    </div>

    <search-owner ref="searchOwner" @chooseOwner="handleChooseOwner" />
    <select-fee-config ref="selectFeeConfig" @notifyFeeConfig="handleNotifyFeeConfig" />
    <room-tree ref="roomTree" @selectRoom="handleChooseRoom" />
  </div>
</template>

<script>
import { getCommunityId } from '@/api/community/communityApi'
import { saveHandover, queryRooms } from '@/api/room/handoverApi'
import SearchOwner from '@/components/owner/SearchOwner'
import SelectFeeConfig from '@/components/room/selectFeeConfig'
import RoomTree from '@/components/room/roomTree'
import { resolveStatus } from '@/utils/statusMap'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'

export default {
  name: 'HandoverList',
  components: {
    SearchOwner,
    SelectFeeConfig,
    RoomTree,
    DsPageHeader,
    DsToolbar,
    DsDataTable,
    DsStatusTag
  },
  data() {
    return {
      handoverInfo: {
        name: '',
        age: '',
        link: '',
        sex: '',
        remark: '',
        ownerId: '',
        idCard: '',
        rooms: [],
        fees: [],
        communityId: ''
      }
    }
  },
  created() {
    this.handoverInfo.communityId = getCommunityId()
  },
  methods: {
    /**
     * 房屋状态语义类型：颜色仅作辅助，始终带文字。
     */
    resolveRoomState(row) {
      if (!row) return 'muted'
      return resolveStatus(row.state, {
        domain: 'room',
        text: row.stateName || ''
      }).type
    },
    selectOwner() {
      this.$refs.searchOwner.open()
    },
    selectRoom() {
      this.$refs.roomTree.open()
    },
    createFeeOrder() {
      this.$refs.selectFeeConfig.open({
        call: 'handover'
      })
    },
    goBack() {
      this.$router.go(-1)
    },
    handleChooseOwner(owner) {
      this.handoverInfo = { ...this.handoverInfo, ...owner }
    },
    handleNotifyFeeConfig(fee) {
      this.handoverInfo.fees.push(fee)
    },
    openDelRoomModel(room) {
      this.handoverInfo.rooms = this.handoverInfo.rooms.filter(item => item.roomId !== room.roomId)
    },
    openDelFeeModel(fee) {
      this.handoverInfo.fees = this.handoverInfo.fees.filter(item => item.configId !== fee.configId)
    },
    async saveHandoverInfo() {
      try {
        const res = await saveHandover(this.handoverInfo)
        if (res.code === 0) {
          this.$message.success(this.$t('common.operationSuccess'))
          this.goBack()
        } else {
          this.$message.error(res.msg)
        }
      } catch (error) {
        this.$message.error(this.$t('handover.submitError'))
      }
    },
    handleChooseRoom(room) {
      this.listRoom(room.roomId)
    },
    async listRoom(roomId) {
      try {
        const params = {
          page: 1,
          row: 1,
          roomId,
          communityId: this.handoverInfo.communityId
        }
        const { data } = await queryRooms(params)
        const room = data.rooms[0]
        if (room.state !== '2002') {
          this.$message.error(this.$t('handover.houseNotUnsold'))
          return
        }
        this.handoverInfo.rooms.push(room)
      } catch (error) {
        this.$message.error(this.$t('handover.fetchRoomError'))
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.handover-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

/* 表格 + 分页 组成的白色面板 */
.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;
  margin-bottom: var(--ds-spacing-md);

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }

  &__body {
    padding: var(--ds-spacing-md) var(--ds-spacing-lg);

    ::v-deep .el-form-item {
      margin-bottom: 0;
    }

    ::v-deep .el-form-item__label {
      padding-bottom: var(--ds-spacing-xs);
      line-height: 1.4;
      color: var(--ds-gray-600);
    }
  }
}

/* 危险操作文字按钮 */
.ds-btn-danger {
  color: var(--ds-danger) !important;
}

.submit-btn {
  margin-top: var(--ds-spacing-lg);
  text-align: right;
}
</style>